import { computed, Injectable, signal } from "@angular/core";
import { AccessLevel, AccessLevels, AccessLevelValue } from "../../../pojo/Fls";
import { RoleRights } from "./role.rights";
import {
    OrgRoleRightOverrideKeyPayload,
    OrgRoleRightOverridePayload,
    RoleRightsConfigService,
} from "./role.rights.config.service";
import { SearchCriteriaService } from "../../../services/search/search-criteria/search.criteria.service";
import {
    ContactRoleOption,
    mapOrganizationRoleItemsToColumns,
    collectContactRolesFromRightsList,
} from "./se.security.baseline.config.store";
import { OrgRoleRights } from "./org.role.rights";

/**
 * Per-organization Connect25 role rights: system defaults (acct_role_right) plus org overrides.
 * Per-cell edits block demotion; matrix “Apply to All” row uses permissive saves (demotions attempted, failures skipped).
 */
@Injectable()
export class SeSecurityOrgConfigStore {
    private orgId: number | null = null;
    private baselineRightsFlat: RoleRights.RightWithRolesConfig[] = [];

    readonly rights = signal<RoleRights.RightWithRolesConfig[]>([]);
    readonly matrixRoleColumns = signal<ContactRoleOption[]>([]);
    readonly loading = signal(true);
    readonly saving = signal(false);
    readonly error = signal<string | null>(null);
    readonly selectedRoleId = signal<number | null>(null);

    readonly overrides = signal<OrgRoleRightOverridePayload[]>([]);

    readonly contactRoles = computed((): ContactRoleOption[] => {
        const map = new Map<number, string>();
        for (const r of this.rights()) {
            for (const a of r.roles) {
                map.set(a.roleId, a.roleName);
            }
        }
        return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1])).map(([id, name]) => ({ id, name }));
    });

    setOrgId(orgId: number): void {
        this.orgId = orgId;
    }

    async load(): Promise<void> {
        if (this.orgId == null) {
            this.error.set("Organization is required.");
            this.loading.set(false);
            return;
        }

        this.loading.set(true);
        this.error.set(null);
        try {
            const [config, orgOverrides, orgRoleItems] = await Promise.all([
                RoleRightsConfigService.getRightsConfig(this.orgId),
                RoleRightsConfigService.getOrgRoleRightOverrides(this.orgId),
                SearchCriteriaService.getOrganizationRoles(false, true),
            ]);
            this.baselineRightsFlat = config;
            this.overrides.set(orgOverrides);

            const matrixCols = mapOrganizationRoleItemsToColumns(orgRoleItems);
            this.matrixRoleColumns.set(matrixCols.length ? matrixCols : collectContactRolesFromRightsList(config));

            this.rights.set(this.buildDenseEffectiveRights());
        } catch (e) {
            console.error("SeSecurityOrgConfigStore.load", e);
            this.error.set("Failed to load organization security configuration.");
            this.rights.set([]);
            this.matrixRoleColumns.set([]);
        } finally {
            this.loading.set(false);
        }
    }

    selectRole(roleId: number | null): void {
        this.selectedRoleId.set(roleId);
    }

    /** System-wide default (acct_role_right) for this role and right — ignores org overrides. */
    globalLevelForRole(rightName: string, roleId: number): AccessLevel {
        return OrgRoleRights.globalLevel(this.baselineRightsFlat, rightName, roleId);
    }

    levelForSelectedRole(rightName: string): AccessLevel {
        const roleId = this.selectedRoleId();
        if (!roleId) return AccessLevels.None;
        return this.levelForRole(rightName, roleId);
    }

    levelForRole(rightName: string, roleId: number): AccessLevel {
        return this.effectiveLevel(rightName, roleId);
    }

    private effectiveLevel(rightName: string, roleId: number): AccessLevel {
        return OrgRoleRights.effectiveLevel(this.baselineRightsFlat, this.overrides(), this.orgId, rightName, roleId);
    }

    private findOverrideRow(rightName: string, roleId: number): OrgRoleRightOverridePayload | undefined {
        return OrgRoleRights.findOverride(this.overrides(), this.orgId, rightName, roleId);
    }

    async setAccessLevelForSelectedRole(rightName: string, level: AccessLevel): Promise<void> {
        const roleId = this.selectedRoleId();
        if (!roleId) return;
        await this.setAccessLevelForRole(rightName, roleId, level);
    }

    async setAccessLevelForRole(
        rightName: string,
        roleId: number,
        level: AccessLevel,
        opts?: { permissive?: boolean; skipSaving?: boolean; suppressError?: boolean },
    ): Promise<void> {
        if (this.orgId == null) return;

        const def = this.baselineRightsFlat.find((r) => r.rightName === rightName);
        if (!def) return;

        const effectiveNow = this.effectiveLevel(rightName, roleId);
        const global = this.globalLevelForRole(rightName, roleId);

        if (!opts?.permissive && AccessLevelValue[level] < AccessLevelValue[effectiveNow]) {
            this.error.set("Access cannot be lowered for this organization.");
            return;
        }

        const hadOverride = !!this.findOverrideRow(rightName, roleId);
        const prevOverrides = this.overrides();
        const prevRights = this.rights();

        const filtered = prevOverrides.filter(
            (x) => !(x.orgId === this.orgId && x.contRoleId === roleId && x.rightName === rightName),
        );
        const nextOverrides =
            AccessLevelValue[level] === AccessLevelValue[global]
                ? filtered
                : [...filtered, { orgId: this.orgId, contRoleId: roleId, rightName, accessLevel: level }];

        this.overrides.set(nextOverrides);
        this.rights.set(this.buildDenseEffectiveRights());

        const trackSaving = !opts?.skipSaving;
        if (trackSaving) this.saving.set(true);
        if (!opts?.suppressError) this.error.set(null);
        try {
            if (AccessLevelValue[level] === AccessLevelValue[global]) {
                if (hadOverride) {
                    //The org no longer wants to have an override of campus default for the given role/right - we can delete or could set to "N"
                    await RoleRightsConfigService.deleteOrgRoleRightOverride(this.orgId, roleId, rightName);
                }
            } else {
                await RoleRightsConfigService.putOrgRoleRightOverrides([
                    { orgId: this.orgId, contRoleId: roleId, rightName, accessLevel: level },
                ]);
            }
        } catch (e) {
            console.error("SeSecurityOrgConfigStore.setAccessLevelForRole", e);
            if (!opts?.suppressError) {
                this.error.set("Failed to save. Reverting.");
            }
            this.overrides.set(prevOverrides);
            this.rights.set(prevRights);
        } finally {
            if (trackSaving) this.saving.set(false);
        }
    }

    /**
     * Matrix "Apply to All" row (org): apply level to this right for each visible role column.
     * Persists with at most one batched DELETE and one batched PUT to {@link RoleRightsConfigService}
     * (same endpoint shape as global role rights: many {@code contRoleId}s in one request body).
     * Skips "Read" when the right does not define read access.
     */
    async applyLevelToVisibleRoleColumnsForRightPermissive(
        rightName: string,
        roleIds: number[],
        level: AccessLevel,
    ): Promise<void> {
        if (!roleIds.length) return;
        const organizationId = this.orgId;
        if (organizationId == null) return;

        const def = this.baselineRightsFlat.find((r) => r.rightName === rightName);
        if (!def) return;
        if (level === AccessLevels.Read && !def.readDescription) return;

        const uniqueRoleIds = [...new Set(roleIds)];

        const prevOverrides = this.overrides();
        const prevRights = this.rights();

        const nextOverrides = this.mergeOrgOverridesForRightAndRoles(
            prevOverrides,
            organizationId,
            rightName,
            uniqueRoleIds,
            level,
        );

        const puts: OrgRoleRightOverridePayload[] = [];
        const deletes: OrgRoleRightOverrideKeyPayload[] = [];

        //
        for (const roleId of uniqueRoleIds) {
            const prevRow = prevOverrides.find(
                (x) => x.orgId === organizationId && x.contRoleId === roleId && x.rightName === rightName,
            );
            const nextRow = nextOverrides.find(
                (x) => x.orgId === organizationId && x.contRoleId === roleId && x.rightName === rightName,
            );
            if (prevRow && !nextRow) {
                deletes.push({ orgId: organizationId, contRoleId: roleId, rightName });
            }
            if (nextRow) {
                puts.push(nextRow);
            }
        }

        if (!puts.length && !deletes.length) {
            return;
        }

        this.overrides.set(nextOverrides);
        this.rights.set(this.buildDenseEffectiveRights());

        this.saving.set(true);
        this.error.set(null);
        try {
            if (deletes.length) {
                //This org no longer has an override for
                await RoleRightsConfigService.deleteOrgRoleRightOverrides(deletes);
            }
            if (puts.length) {
                await RoleRightsConfigService.putOrgRoleRightOverrides(puts);
            }
        } catch (e) {
            console.error("SeSecurityOrgConfigStore.applyLevelToVisibleRoleColumnsForRightPermissive", e);
            this.error.set("Failed to save. Reverting.");
            this.overrides.set(prevOverrides);
            this.rights.set(prevRights);
        } finally {
            this.saving.set(false);
        }
    }

    /**
     * Applies the same target level for one right across many roles (in-memory overrides list only).
     * Independent keys ({@code contRoleId}) — order does not matter.
     */
    private mergeOrgOverridesForRightAndRoles(
        prevOverrides: OrgRoleRightOverridePayload[],
        organizationId: number,
        rightName: string,
        roleIds: number[],
        level: AccessLevel,
    ): OrgRoleRightOverridePayload[] {
        const compositeKey = (roleId: number) => `${organizationId}|${roleId}|${rightName}`;
        const byCompositeKey = new Map<string, OrgRoleRightOverridePayload>();
        for (const row of prevOverrides) {
            byCompositeKey.set(`${row.orgId}|${row.contRoleId}|${row.rightName}`, row);
        }
        for (const roleId of roleIds) {
            byCompositeKey.delete(compositeKey(roleId));
            const global = this.globalLevelForRole(rightName, roleId);
            if (AccessLevelValue[level] !== AccessLevelValue[global]) {
                byCompositeKey.set(compositeKey(roleId), {
                    orgId: organizationId,
                    contRoleId: roleId,
                    rightName,
                    accessLevel: level,
                });
            }
        }
        return [...byCompositeKey.values()];
    }

    private buildDenseEffectiveRights(): RoleRights.RightWithRolesConfig[] {
        const cols = this.matrixRoleColumns();
        return this.baselineRightsFlat.map((def) => ({
            ...def,
            roles: cols.map((col) => ({
                roleId: col.id,
                roleName: col.name,
                accessLevel: this.effectiveLevel(def.rightName, col.id),
            })),
        }));
    }

    /**
     * Baseline right definitions (metadata + default role columns) for layered UIs such as
     * per-contact grants, without a second GET /role/rights/config.
     */
    getBaselineRightDefinitions(): RoleRights.RightWithRolesConfig[] {
        return this.baselineRightsFlat.map((def) => ({
            ...def,
            roles: def.roles.map((r) => ({ ...r })),
        }));
    }
}
